import * as React from 'react'
import queryString from 'query-string'
import { useHistory, useLocation } from 'react-router'

import { MFAResponse, UserAuth, UserAuthCompany } from 'api'
import authentication from 'api/authentication'
import toast from 'lib/toast'
import { messageFromResponseError } from 'utils/strings'
import useAuth from 'hooks/use-auth'
import useAppRefresh from 'hooks/use-app-refresh'
import SignInForm from 'components/authentication/SignInForm'
import MFAForm from 'components/authentication/MFAForm'
import CompanyListForm from 'components/authentication/CompanyListForm'
import Modal, { ModalHeader, ModalCloseButton } from 'components/Modal'
import MFATokenEntry from 'components/profile/MFATokenEntry'

enum ACTIONS {
  RESET = 'reset',
  ENTER_TOKEN = 'set_token',
  SELECT_COMPANY = 'set_company',
  VALIDATE_CREDENTIALS = 'validate_credentials',
  UPDATE_EMAIL = 'update_email',
  UPDATE_PASSWORD = 'update_password',
  UPDATE_TOKEN = 'update_token',
  UPDATE_COMPANY_NAME = 'update_company_name'
}

type Mode = 'credentials' | 'token' | 'companies'

type State = {
  mode: Mode
  email: string
  password: string
  token: string
  companyName: string
  companyList: UserAuthCompany[]
  mfaResponse: MFAResponse | UserAuth | undefined
}

type Action =
  | { type: ACTIONS.RESET }
  | { type: ACTIONS.ENTER_TOKEN; mfaResponse?: MFAResponse }
  | { type: ACTIONS.SELECT_COMPANY; companyList: UserAuthCompany[] }
  | { type: ACTIONS.VALIDATE_CREDENTIALS; mfaResponse: MFAResponse | UserAuth }
  | { type: ACTIONS.UPDATE_EMAIL; email: string }
  | { type: ACTIONS.UPDATE_PASSWORD; password: string }
  | { type: ACTIONS.UPDATE_TOKEN; token: string }
  | { type: ACTIONS.UPDATE_COMPANY_NAME; companyName: string }

const initialState: State = {
  mode: 'credentials',
  email: '',
  password: '',
  token: '',
  companyName: '',
  companyList: [],
  mfaResponse: null
}

const signInReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ACTIONS.RESET:
      return {
        ...initialState
      }

    case ACTIONS.ENTER_TOKEN:
      return {
        ...state,
        mode: 'token',
        mfaResponse: null
      }

    case ACTIONS.SELECT_COMPANY:
      return {
        ...state,
        mode: 'companies',
        companyList: action.companyList,
        mfaResponse: null
      }

    case ACTIONS.VALIDATE_CREDENTIALS:
      return {
        ...state,
        mode: 'credentials',
        mfaResponse: action.mfaResponse
      }

    case ACTIONS.UPDATE_EMAIL:
      return {
        ...state,
        email: action.email
      }

    case ACTIONS.UPDATE_PASSWORD:
      return {
        ...state,
        password: action.password
      }

    case ACTIONS.UPDATE_TOKEN:
      return {
        ...state,
        token: action.token
      }

    case ACTIONS.UPDATE_COMPANY_NAME:
      return {
        ...state,
        companyName: action.companyName
      }

    default:
      return state
  }
}

const SignIn: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const authState = useAuth()
  const appRefresh = useAppRefresh()

  const [state, dispatch] = React.useReducer(signInReducer, initialState)
  const [submitting, setSubmitting] = React.useState(false)

  const handlePasswordLogin = async (selectedCompany = state.companyName) => {
    try {
      dispatch({
        type: ACTIONS.UPDATE_COMPANY_NAME,
        companyName: selectedCompany
      })
      setSubmitting(true)

      const credentials: UserAuth = await authentication.signin({
        email: state.email,
        password: state.password,
        token: state.token,
        companyName: selectedCompany
      })

      if (credentials === 'useMfaAuth') {
        dispatch({ type: ACTIONS.ENTER_TOKEN })
        setSubmitting(false)
        return
      } else if (credentials && credentials.result === 'chooseCompany') {
        dispatch({
          type: ACTIONS.SELECT_COMPANY,
          companyList: credentials.companies
        })
        setSubmitting(false)
        return
      } else if (credentials?.qrUrl && credentials.secretKey) {
        // Need to validate your mfaToken
        dispatch({
          type: ACTIONS.VALIDATE_CREDENTIALS,
          mfaResponse: credentials
        })
        setSubmitting(false)
        return
      }

      await authState.startSession(credentials)

      // Refresh Webapp When Logging In
      // since we're setting the href explicitly below, that reloads the webapp
      // so if it's marked as needing to be refreshed, then we can clear that here
      appRefresh.markAsRefreshed()

      const { next } = queryString.parse(location.search)
      if (next) {
        window.location.href = Array.isArray(next) ? next[0] : next
      } else {
        window.location.href = '/'
      }
    } catch (err) {
      handleError(err, 'Invalid username or password.')
      setSubmitting(false)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      const { next } = queryString.parse(location.search)

      // same as above with password login
      // we're refreshing the webapp since we reload the page here
      appRefresh.markAsRefreshed()

      window.location.href = await authentication.generateGoogleUrl({
        nextPathname: next
      })
    } catch (err) {
      handleError(
        err,
        'There was an issue communicating with Google. Please try again later.'
      )
    }
  }

  const handleSamlLogin = async () => {
    history.push('/login/saml')
  }

  const handleError = (err, defaultText) => {
    const errorText = messageFromResponseError(err, defaultText)

    toast({
      type: 'error',
      text: errorText,
      time: 10000,
      testKey: 'signin'
    })
  }

  if (state.mode === 'token') {
    return (
      <MFAForm
        handleBack={() => dispatch({ type: ACTIONS.RESET })}
        handleTokenChange={token =>
          dispatch({ type: ACTIONS.UPDATE_TOKEN, token })
        }
        handleTokenSubmission={handlePasswordLogin}
        submitting={submitting}
        token={state.token}
      />
    )
  }

  if (state.mode === 'companies') {
    return (
      <CompanyListForm
        companies={state.companyList}
        handleBack={() => dispatch({ type: ACTIONS.RESET })}
        handleCompanySelection={companyName => handlePasswordLogin(companyName)}
        submitting={submitting}
      />
    )
  }

  if (state.mfaResponse) {
    return (
      <Modal isOpen={Boolean(state.mfaResponse)}>
        <ModalHeader>
          <ModalCloseButton onClick={() => location.reload()} />
        </ModalHeader>
        <MFATokenEntry
          isSignIn
          email={state.email}
          mfaResponse={state.mfaResponse}
          refresh={() => location.reload()}
          validateMFASuccess={handlePasswordLogin}
        />
      </Modal>
    )
  }

  return (
    <div>
      <SignInForm
        email={state.email}
        handleEmailChange={email =>
          dispatch({ type: ACTIONS.UPDATE_EMAIL, email })
        }
        handleGoogleLogin={handleGoogleLogin}
        handlePasswordChange={password =>
          dispatch({ type: ACTIONS.UPDATE_PASSWORD, password })
        }
        handlePasswordLogin={handlePasswordLogin}
        handleSamlLogin={handleSamlLogin}
        password={state.password}
        submitting={submitting}
      />
    </div>
  )
}

export default SignIn
